import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, FileText, Loader2, Lock, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { INTAKE_BUCKET, INTAKE_KIND_LABELS, isValidCpf, onlyDigits } from '@/lib/clientIntake';

const MAX_FILE_BYTES = 15 * 1024 * 1024;
const MARITAL = ['Solteiro(a)', 'Casado(a)', 'União estável', 'Divorciado(a)', 'Separado(a)', 'Viúvo(a)'];
const STATES = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
const DOCUMENTS = [
  { kind: 'identidade', hint: 'RG ou CNH, frente e verso. Obrigatório.', required: true },
  { kind: 'cpf', hint: 'Só se o número não aparecer no documento com foto.', required: false },
  { kind: 'residencia', hint: 'Conta de luz, água ou telefone recente.', required: false },
  { kind: 'outros', hint: 'Qualquer documento ligado ao seu caso.', required: false },
];

const EMPTY = {
  nome: '', cpf: '', rg: '', nascimento: '', estado_civil: '', profissao: '', nacionalidade: 'Brasileira', email: '', celular: '',
  cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '',
};

type Stage = 'loading' | 'form' | 'invalid' | 'done';
interface SentFile { path: string; kind: string; name: string }

const call = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke('client-intake', { body });
  if (error) throw new Error('Não foi possível falar com o sistema. Verifique sua internet e tente de novo.');
  return data as any;
};

const maskCpf = (value: string) => {
  const d = onlyDigits(value).slice(0, 11);
  return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
};
const maskPhone = (value: string) => {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};
const maskCep = (value: string) => {
  const d = onlyDigits(value).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
};

const Field = ({ id, label, optional, children }: { id?: string; label: string; optional?: boolean; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-slate-700">{label}{optional && <span className="ml-1 font-normal text-slate-400">(opcional)</span>}</Label>
    {children}
  </div>
);

const Section = ({ number, title, children }: { number: number; title: string; children: React.ReactNode }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
    <h2 className="mb-4 flex items-center gap-2.5 text-base font-semibold text-slate-900">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#c4ab83]/25 text-xs font-semibold text-[#7d6640]">{number}</span>
      {title}
    </h2>
    {children}
  </section>
);

const ClientIntakeForm = () => {
  const { token = '' } = useParams();
  const [stage, setStage] = useState<Stage>('loading');
  const [message, setMessage] = useState('');
  const [consentText, setConsentText] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState<SentFile[]>([]);
  const [uploading, setUploading] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    document.title = 'Cadastro · Allana Sthéfany Advocacia';
    (async () => {
      try {
        const result = await call({ action: 'get', token });
        if (!result?.ok) {
          setMessage(result?.error || 'Este link não é válido.');
          setStage('invalid');
          return;
        }
        if (result.submitted) {
          setStage('done');
          return;
        }
        setConsentText(result.consentText);
        const label = String(result.label || '');
        if (label.trim().split(/\s+/).length >= 2) setForm((f) => ({ ...f, nome: label }));
        setStage('form');
      } catch (e) {
        setMessage((e as Error).message);
        setStage('invalid');
      }
    })();
  }, [token]);

  const set = (field: keyof typeof EMPTY, value: string) => setForm((f) => ({ ...f, [field]: value }));

  const onCep = async (value: string) => {
    const masked = maskCep(value);
    set('cep', masked);
    const cep = onlyDigits(masked);
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data?.erro) return;
      setForm((f) => ({
        ...f,
        endereco: data.logradouro || f.endereco,
        bairro: data.bairro || f.bairro,
        cidade: data.localidade || f.cidade,
        uf: data.uf || f.uf,
      }));
    } catch {
      // Sem a consulta, a pessoa preenche o endereço à mão
    }
  };

  const onFiles = async (kind: string, list: FileList | null) => {
    if (!list?.length) return;
    setError('');
    setUploading(kind);
    try {
      for (const file of Array.from(list)) {
        if (file.size > MAX_FILE_BYTES) throw new Error(`"${file.name}" tem mais de 15 MB. Envie um arquivo menor.`);
        const type = file.type || (/\.hei[cf]$/i.test(file.name) ? 'image/heic' : '');
        const signed = await call({ action: 'upload-url', token, kind, type });
        if (!signed?.ok) throw new Error(signed?.error || 'Não foi possível enviar o arquivo.');
        const { error: uploadError } = await supabase.storage.from(INTAKE_BUCKET).uploadToSignedUrl(signed.path, signed.uploadToken, file, { contentType: type });
        if (uploadError) throw new Error(`Não foi possível enviar "${file.name}". Tente de novo.`);
        setFiles((current) => [...current, { path: signed.path, kind, name: file.name }]);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(null);
      if (inputs.current[kind]) inputs.current[kind]!.value = '';
    }
  };

  const removeFile = async (file: SentFile) => {
    setError('');
    try {
      const result = await call({ action: 'remove-file', token, path: file.path });
      if (!result?.ok) throw new Error(result?.error);
      setFiles((current) => current.filter((f) => f.path !== file.path));
    } catch {
      setError('Não foi possível remover o arquivo. Tente de novo.');
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!isValidCpf(form.cpf)) return setError('Confira o CPF: o número informado não é válido.');
    if (!files.some((f) => f.kind === 'identidade')) return setError('Envie um documento com foto para continuar.');
    if (!form.estado_civil || !form.uf) return setError('Preencha o estado civil e o estado do endereço.');
    setSending(true);
    try {
      const result = await call({ action: 'submit', token, data: form, consent });
      if (!result?.ok) throw new Error(result?.error);
      setStage('done');
      window.scrollTo({ top: 0 });
    } catch (e) {
      setError((e as Error).message || 'Não foi possível enviar. Tente de novo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f2] px-4 py-8 sm:py-12">
      <div className="mx-auto max-w-2xl">
        <header className="mb-6 text-center">
          <img src="/imgs/logos allana/logoasad.png" alt="Allana Sthéfany Advocacia" className="mx-auto h-16 w-16 object-contain" />
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900">Allana Sthéfany Advocacia</h1>
        </header>

        {stage === 'loading' && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#9a7f52]" /></div>}

        {stage === 'invalid' && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <p className="text-sm text-slate-700">{message}</p>
          </div>
        )}

        {stage === 'done' && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-[#9a7f52]" />
            <h2 className="mt-3 text-lg font-semibold text-slate-900">Cadastro recebido</h2>
            <p className="mt-1 text-sm text-slate-600">Obrigada! Vamos conferir seus dados e entraremos em contato com os próximos passos.</p>
          </div>
        )}

        {stage === 'form' && (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-center text-sm text-slate-600">Preencha seus dados e envie seus documentos para darmos início ao seu atendimento.</p>

            <Section number={1} title="Seus dados">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Field id="nome" label="Nome completo"><Input id="nome" required autoComplete="name" value={form.nome} onChange={(e) => set('nome', e.target.value)} /></Field></div>
                <Field id="cpf" label="CPF"><Input id="cpf" required inputMode="numeric" value={form.cpf} onChange={(e) => set('cpf', maskCpf(e.target.value))} placeholder="000.000.000-00" /></Field>
                <Field id="rg" label="RG" optional><Input id="rg" value={form.rg} onChange={(e) => set('rg', e.target.value)} /></Field>
                <Field id="nascimento" label="Data de nascimento"><Input id="nascimento" type="date" required autoComplete="bday" value={form.nascimento} onChange={(e) => set('nascimento', e.target.value)} /></Field>
                <Field label="Estado civil">
                  <Select value={form.estado_civil} onValueChange={(v) => set('estado_civil', v)}>
                    <SelectTrigger aria-label="Estado civil"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{MARITAL.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field id="profissao" label="Profissão"><Input id="profissao" required value={form.profissao} onChange={(e) => set('profissao', e.target.value)} /></Field>
                <Field id="nacionalidade" label="Nacionalidade"><Input id="nacionalidade" required value={form.nacionalidade} onChange={(e) => set('nacionalidade', e.target.value)} /></Field>
                <Field id="celular" label="Celular com WhatsApp"><Input id="celular" required type="tel" autoComplete="tel" value={form.celular} onChange={(e) => set('celular', maskPhone(e.target.value))} placeholder="(81) 99999-9999" /></Field>
                <Field id="email" label="E-mail"><Input id="email" required type="email" autoComplete="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
              </div>
            </Section>

            <Section number={2} title="Endereço">
              <div className="grid gap-4 sm:grid-cols-6">
                <div className="sm:col-span-2"><Field id="cep" label="CEP"><Input id="cep" required inputMode="numeric" autoComplete="postal-code" value={form.cep} onChange={(e) => onCep(e.target.value)} placeholder="00000-000" /></Field></div>
                <div className="sm:col-span-4"><Field id="endereco" label="Rua"><Input id="endereco" required autoComplete="street-address" value={form.endereco} onChange={(e) => set('endereco', e.target.value)} /></Field></div>
                <div className="sm:col-span-2"><Field id="numero" label="Número"><Input id="numero" required value={form.numero} onChange={(e) => set('numero', e.target.value)} /></Field></div>
                <div className="sm:col-span-4"><Field id="complemento" label="Complemento" optional><Input id="complemento" value={form.complemento} onChange={(e) => set('complemento', e.target.value)} /></Field></div>
                <div className="sm:col-span-2"><Field id="bairro" label="Bairro"><Input id="bairro" required value={form.bairro} onChange={(e) => set('bairro', e.target.value)} /></Field></div>
                <div className="sm:col-span-3"><Field id="cidade" label="Cidade"><Input id="cidade" required value={form.cidade} onChange={(e) => set('cidade', e.target.value)} /></Field></div>
                <div className="sm:col-span-1">
                  <Field label="UF">
                    <Select value={form.uf} onValueChange={(v) => set('uf', v)}>
                      <SelectTrigger aria-label="Estado"><SelectValue placeholder="UF" /></SelectTrigger>
                      <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>
            </Section>

            <Section number={3} title="Documentos">
              <p className="mb-3 text-xs text-slate-500">Envie em PDF ou tire uma foto com o celular. Cada arquivo pode ter até 15 MB.</p>
              <ul className="space-y-3">
                {DOCUMENTS.map(({ kind, hint, required }) => {
                  const sent = files.filter((f) => f.kind === kind);
                  return (
                    <li key={kind} className="rounded-xl border border-slate-200 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800">{INTAKE_KIND_LABELS[kind]}{required && <span className="text-red-600"> *</span>}</p>
                          <p className="text-xs text-slate-500">{hint}</p>
                        </div>
                        <Button type="button" variant="outline" size="sm" disabled={uploading !== null} onClick={() => inputs.current[kind]?.click()}>
                          {uploading === kind ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
                          Enviar
                        </Button>
                        <input
                          ref={(el) => { inputs.current[kind] = el; }}
                          type="file"
                          multiple
                          accept="application/pdf,image/*"
                          className="hidden"
                          aria-label={INTAKE_KIND_LABELS[kind]}
                          onChange={(e) => onFiles(kind, e.target.files)}
                        />
                      </div>
                      {sent.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {sent.map((file) => (
                            <li key={file.path} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700">
                              <span className="flex min-w-0 items-center gap-1.5"><FileText className="h-3.5 w-3.5 shrink-0 text-[#9a7f52]" /><span className="truncate">{file.name}</span></span>
                              <button type="button" onClick={() => removeFile(file)} aria-label={`Remover ${file.name}`} className="shrink-0 p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Section>

            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
              <span>{consentText}</span>
            </label>

            {error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}

            <Button type="submit" disabled={sending || uploading !== null || !consent} className="h-11 w-full rounded-xl bg-slate-900 text-white hover:bg-slate-800">
              {sending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enviando...</> : 'Enviar cadastro'}
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500"><Lock className="h-3 w-3" />Seus dados e documentos ficam em área privada, acessível só ao escritório.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClientIntakeForm;
